import { useQuery } from '@tanstack/react-query';
import { graphqlFetch } from '../../lib/graphql-fetch';
import { LIST_ACTIVE_PACKS_QUERY, GET_PACK_QUERY } from '../../lib/graphql-queries';
import type { PackArtFields } from './types';

/**
 * Pack-feature TanStack Query hooks.
 *
 * Key conventions per frontend.md §15.4:
 *  - `['packs', 'active']`
 *  - `['pack', pack_id]`
 *
 * `staleTime` of 30s matches the default in `lib/query-client.ts`.
 *
 * Lever H (Round 3.5): both fetchActivePacks AND fetchPack use the
 * lightweight graphqlFetch helper instead of the Amplify Data client.
 * This evicts @aws-amplify/api-graphql + @aws-amplify/data-schema +
 * graphql/language internals from the entry chunk. DO NOT add back
 * `import { client } from '../../lib/amplify-client'` to this file
 * unless you also re-measure the entry-chunk size — the import alone
 * (via amplify-client.ts's eager generateClient<Schema>() call) drags
 * the whole tree back.
 */

export const PACKS_ACTIVE_QUERY_KEY = ['packs', 'active'] as const;

export interface Pack extends PackArtFields {
  name: string;
  set_slug: string;
  price_cents: number;
  cards_per_pack: number;
  description?: string | null;
}

async function fetchActivePacks(): Promise<Pack[]> {
  const data = await graphqlFetch<{ listActivePacks: (Pack | null)[] }>(
    LIST_ACTIVE_PACKS_QUERY,
  );
  return (data.listActivePacks ?? []).filter((p): p is Pack => p !== null);
}

async function fetchPack(packId: string): Promise<Pack | null> {
  const data = await graphqlFetch<{ getPack: Pack | null }>(
    GET_PACK_QUERY,
    { pack_id: packId },
  );
  return data.getPack ?? null;
}

export function useListActivePacks() {
  return useQuery({
    queryKey: PACKS_ACTIVE_QUERY_KEY,
    queryFn: fetchActivePacks,
    staleTime: 30_000,
  });
}

export function useGetPack(packId: string | undefined) {
  return useQuery({
    queryKey: ['pack', packId] as const,
    queryFn: () => {
      if (!packId) return Promise.resolve(null);
      return fetchPack(packId);
    },
    enabled: typeof packId === 'string' && packId.length > 0,
    staleTime: 30_000,
  });
}
